'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LastChanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpgrade: () => void;
    onContinueFree: () => void;
    accountType: 'personal' | 'business';
    planName?: string;
}

// Enhanced value propositions for personal users - emphasizing what they'll miss
const personalMissedFeatures = [
    {
        icon: '4k',
        title: 'Stunning 4K quality streaming',
        description: 'Crystal clear art on any screen',
    },
    {
        icon: 'playlist',
        title: 'Unlimited playlists',
        description: 'Curate art collections for every mood',
    },
    {
        icon: 'heart',
        title: 'Full art library access',
        description: 'Thousands of works from real artists',
    },
    {
        icon: 'support',
        title: 'Priority support',
        description: 'Get help when you need it most',
    },
];

// Enhanced value propositions for business users - emphasizing ROI and professionalism
const businessMissedFeatures = [
    {
        icon: 'license',
        title: 'Commercial licensing included',
        description: 'No legal risks for public display',
    },
    {
        icon: '4k',
        title: '4K playlists for your industry',
        description: 'Curated content that matches your brand',
    },
    {
        icon: 'brand',
        title: 'Branding tools with your logo',
        description: 'Customize the experience with your identity',
    },
    {
        icon: 'multi',
        title: 'Multi-location management',
        description: 'Control all your screens from one dashboard',
    },
];

// Icon component for missed features
function FeatureIcon({ type }: { type: string }) {
    switch (type) {
        case 'license':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                </svg>
            );
        case '4k':
            return (
                <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-purple-400 bg-purple-400/20 rounded">
                    4K
                </span>
            );
        case 'playlist':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                    />
                </svg>
            );
        case 'heart':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                </svg>
            );
        case 'brand':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                    />
                </svg>
            );
        case 'support':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                </svg>
            );
        case 'roi':
            return (
                <svg
                    className="w-6 h-6 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                </svg>
            );
        case 'analytics':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                </svg>
            );
        case 'multi':
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                </svg>
            );
        default:
            return (
                <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            );
    }
}

export function LastChanceModal({
    isOpen,
    onClose,
    onUpgrade,
    onContinueFree,
    accountType = 'personal',
    planName = 'Basic',
}: LastChanceModalProps) {
    const [showFeatures, setShowFeatures] = useState<boolean[]>([]);

    // Select features based on account type
    const missedFeatures =
        accountType === 'business'
            ? businessMissedFeatures
            : personalMissedFeatures;

    useEffect(() => {
        if (isOpen) {
            // Animate features appearing one by one
            setShowFeatures([]);
            missedFeatures.forEach((_, index) => {
                setTimeout(() => {
                    setShowFeatures((prev) => [...prev, true]);
                }, 200 * (index + 1));
            });
        } else {
            setShowFeatures([]);
        }
    }, [isOpen, missedFeatures]);

    if (!isOpen) return null;

    // Content varies by account type
    const content = {
        personal: {
            headline: 'Before you continue with the free plan',
            subheadline: 'The free plan is very limited.',
            whatYouMiss: "You'll miss:",
            ctaText: 'START YOUR FREE TRIAL',
            ctaSubtext: 'Cancel anytime. Risk-free.',
            declineText: 'Continue with the free plan',
            urgencyText: 'Limited time offer',
            testimonial: {
                quote: '"The quality difference is night and day. My living room feels like a gallery now."',
                author: '— Sarah M., Niio Member',
            },
        },
        business: {
            headline: 'Before you continue with the free plan',
            subheadline: 'Your business deserves better.',
            whatYouMiss: "You'll miss:",
            ctaText: 'START YOUR FREE TRIAL',
            ctaSubtext: 'Cancel anytime. Risk-free.',
            declineText: 'Continue with the free plan',
            urgencyText: 'Enterprise discount available',
            testimonial: {
                quote: '"Niio transformed our lobbies. Guests comment on the art every day."',
                author: '— Michael R., Hotel Manager',
            },
        },
    };

    const c = content[accountType];

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 rounded-2xl max-w-md w-full p-6 sm:p-8 text-center relative border border-gray-700 shadow-2xl max-h-[90vh] overflow-y-auto"
                >
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
                        aria-label="Close"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>

                    {/* Warning Icon */}
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full border-4 border-purple-500 flex items-center justify-center">
                                <span className="text-purple-500 text-2xl font-bold">
                                    !
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Headline */}
                    <div className="mb-4">
                        <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                            {c.headline}
                        </h2>
                        <p className="text-gray-400 text-sm">{c.subheadline}</p>
                    </div>

                    {/* What you'll miss section */}
                    <div className="text-left mb-5">
                        <p className="text-gray-300 text-sm mb-3 font-medium">
                            {c.whatYouMiss}
                        </p>
                        <ul className="space-y-3">
                            {missedFeatures.map((feature, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{
                                        opacity: showFeatures[index] ? 1 : 0,
                                        x: showFeatures[index] ? 0 : -10,
                                    }}
                                    transition={{ duration: 0.3 }}
                                    className="flex items-start"
                                >
                                    <span className="mr-3 flex-shrink-0 mt-0.5">
                                        <FeatureIcon type={feature.icon} />
                                    </span>
                                    <div>
                                        <p className="text-white text-sm font-medium">
                                            {feature.title}
                                        </p>
                                        <p className="text-gray-500 text-xs">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>
                    </div>

                    {/* Testimonial */}
                    <div className="bg-white/5 rounded-lg p-4 mb-5 border-l-4 border-purple-500 text-left">
                        <p className="text-gray-300 text-sm italic mb-2">
                            {c.testimonial.quote}
                        </p>
                        <p className="text-purple-400 text-xs font-medium">
                            {c.testimonial.author}
                        </p>
                    </div>

                    {/* Primary CTA */}
                    <motion.button
                        onClick={onUpgrade}
                        className="w-full bg-purple-500 hover:bg-purple-400 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-purple-500/30 mb-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <span className="block text-base">{c.ctaText}</span>
                        <span className="block text-xs font-normal text-purple-200">
                            {c.ctaSubtext}
                        </span>
                    </motion.button>

                    {/* Decline Option */}
                    <div className="mt-3">
                        <button
                            onClick={onContinueFree}
                            className="w-full bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium py-3 px-6 rounded-xl transition-colors text-sm"
                        >
                            {c.declineText}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
